import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { useUserStore } from '../store/userStore'
import { useLanguageStore } from '../store/languageStore'
import {
  getActiveStudyPlan,
  createStudyPlan,
  updateStudyPlan,
  cancelStudyPlan,
  APIError,
  type StudyPlan as StudyPlanType,
  type StudyPlanStats,
  getDailyStudyLogs,
  type DailyStudyLog,
} from '../services/api'

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const DAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAY_INITIALS = ['S', 'M', 'T', 'W', 'R', 'F', 'S']
const DEFAULT_STUDY_DAYS: number[] = [1, 2, 3, 4, 5]
const DEFAULT_GOAL_HOURS = 50
const LANGUAGE_COLORS = ['#2563eb', '#a855f7', '#f59e0b', '#10b981', '#ef4444', '#0ea5e9', '#f97316', '#14b8a6']
const GOAL_SUGGESTIONS: Array<{ hours: number; label: string }> = [
  { hours: 50, label: 'Basic travel understanding' },
  { hours: 200, label: 'Comfortable tourist conversations' },
  { hours: 500, label: 'Confident everyday chatting' },
  { hours: 1000, label: 'Strong conversational skills' },
  { hours: 5000, label: 'High fluency / near-native' },
]

export function StudyPlan() {
  const navigate = useNavigate()
  const { user } = useUserStore()
  const { currentLanguage, availableLanguages } = useLanguageStore()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activePlan, setActivePlan] = useState<{ plan: StudyPlanType; stats: StudyPlanStats } | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  // Form state for creating/editing a plan
  const [calculationMode, setCalculationMode] = useState<'forward' | 'reverse'>('forward')
  const [goalHours, setGoalHours] = useState<number>(DEFAULT_GOAL_HOURS)
  const [targetDate, setTargetDate] = useState<string>(getDefaultTargetDate())
  const [dailyMinutes, setDailyMinutes] = useState<number>(30)
  const [selectedDays, setSelectedDays] = useState<number[]>(() => [...DEFAULT_STUDY_DAYS]) // Mon-Fri by default
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [studyLogs, setStudyLogs] = useState<DailyStudyLog[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)
  const [logsError, setLogsError] = useState<string | null>(null)
  const [weekOffset, setWeekOffset] = useState(0) // 0 = current week, negative = previous weeks
  const [selectedDayDetail, setSelectedDayDetail] = useState<{
    date: string
    totalMinutes: number
    spokenMinutes: number
    flashcardMinutes: number
    languages: Array<{ language: string; spoken: number; flash: number; total: number }>
  } | null>(null)
  const [showOtherLanguagePlan, setShowOtherLanguagePlan] = useState(false)
  const [forceShowCurrentLanguagePlanForm, setForceShowCurrentLanguagePlanForm] = useState(false)

  // Check if user has access to study plans (Standard or Premium)
  const hasAccess = user?.subscriptionTier === 'standard' || user?.subscriptionTier === 'premium'

  const resetFormToDefaults = useCallback(() => {
    setGoalHours(DEFAULT_GOAL_HOURS)
    setTargetDate(getDefaultTargetDate())
    setSelectedDays([...DEFAULT_STUDY_DAYS])
  }, [])

  const loadStudyLogs = useCallback(async () => {
    if (!user) return

    setIsLoadingLogs(true)
    setLogsError(null)

    // Use a UTC-aware window so "today" logs stored in UTC still show up for users in negative timezones
    const todayLocal = startOfDay(new Date())
    const end = startOfDay(new Date(todayLocal))
    end.setDate(end.getDate() + 1) // include tomorrow to capture UTC-dated "today" logs
    const start = startOfDay(new Date(end))
    start.setDate(end.getDate() - 364) // last 12 months

    try {
      const logs = await getDailyStudyLogs(user.id, toISODate(start), toISODate(end))
      setStudyLogs(logs)
    } catch (err) {
      if (err instanceof APIError) {
        setLogsError(err.message)
      } else {
        setLogsError('Failed to load study analytics')
      }
    } finally {
      setIsLoadingLogs(false)
    }
  }, [user])

  // Load active study plan
  useEffect(() => {
    // Only load if user exists and has access (Standard or Premium)
    if (user && hasAccess) {
      loadActivePlan()
    } else {
      setIsLoading(false)
    }
  }, [user, hasAccess])

  useEffect(() => {
    if (!activePlan?.plan && !isEditing) {
      resetFormToDefaults()
    }
  }, [activePlan?.plan, isEditing, resetFormToDefaults])

  useEffect(() => {
    if (user) {
      loadStudyLogs()
    } else {
      setStudyLogs([])
    }
  }, [user, loadStudyLogs])

  useEffect(() => {
    if (!activePlan?.plan) {
      setShowOtherLanguagePlan(false)
      setForceShowCurrentLanguagePlanForm(false)
      return
    }

    if (!currentLanguage || activePlan.plan.languageCode === currentLanguage) {
      setShowOtherLanguagePlan(true)
      setForceShowCurrentLanguagePlanForm(false)
    } else {
      setShowOtherLanguagePlan(false)
    }
  }, [activePlan?.plan?.id, activePlan?.plan?.languageCode, currentLanguage])

  async function loadActivePlan() {
    if (!user) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await getActiveStudyPlan()
      setActivePlan(result)
    } catch (err) {
      if (err instanceof APIError) {
        if (err.code === 'UNAUTHORIZED') {
          // User is not premium, don't show error
          setActivePlan(null)
        } else {
          setError(err.message)
        }
      } else {
        setError('Failed to load study plan')
      }
    } finally {
      setIsLoading(false)
    }
  }

  function toggleDay(day: number) {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => a - b)
    )
  }

  async function handleCreatePlan(e: React.FormEvent) {
    e.preventDefault()

    if (selectedDays.length === 0) {
      setError('Please select at least one study day')
      return
    }

    if (!currentLanguage) {
      setError('Please select a language first')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      if (calculationMode === 'forward') {
        // Forward mode: user sets target date, we calculate daily minutes
        await createStudyPlan({
          languageCode: currentLanguage,
          goalHours,
          targetDate,
          studyDays: selectedDays,
        })
      } else {
        // Reverse mode: user sets daily minutes, we calculate target date
        await createStudyPlan({
          languageCode: currentLanguage,
          goalHours,
          dailyStudyMinutes: dailyMinutes,
          studyDays: selectedDays,
        })
      }

      // Reload the active plan
      await loadActivePlan()
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Failed to create study plan')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleCancelPlan() {
    if (!activePlan || !window.confirm('Are you sure you want to cancel this study plan?')) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await cancelStudyPlan(activePlan.plan.id)
      await loadActivePlan()
      setIsEditing(false)
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Failed to cancel study plan')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  function startEditingPlan() {
    if (!activePlan?.plan) return
    setGoalHours(activePlan.plan.goalHours)
    setTargetDate(formatDateForInput(activePlan.plan.targetDate))
    setSelectedDays([...activePlan.plan.studyDays].sort((a, b) => a - b))
    setIsEditing(true)
  }

  function stopEditingPlan() {
    setIsEditing(false)
    resetFormToDefaults()
  }

  async function handleUpdatePlan(e: React.FormEvent) {
    e.preventDefault()
    if (!activePlan?.plan) return

    if (selectedDays.length === 0) {
      setError('Please select at least one study day')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // For updates, we only support forward mode for simplicity
      await updateStudyPlan(activePlan.plan.id, {
        goalHours,
        targetDate,
        studyDays: selectedDays,
      })
      await loadActivePlan()
      setIsEditing(false)
      resetFormToDefaults()
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Failed to update study plan')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  function formatHours(hours: number): string {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`
    }
    return `${hours.toFixed(1)} hours`
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const heatmapData = useMemo(() => buildHeatmap(studyLogs), [studyLogs])
  const mobileHeatmapMonths = useMemo(() => buildMobileHeatmapMonths(studyLogs), [studyLogs])
  const barChartData = useMemo(
    () => buildBarSeries(studyLogs, 'week', weekOffset),
    [studyLogs, weekOffset]
  )
  const currentWeekRangeLabel = useMemo(() => {
    const baseStart = startOfWeekSunday(new Date())
    baseStart.setDate(baseStart.getDate() + weekOffset * 7)
    const start = startOfDay(new Date(baseStart))
    const end = startOfDay(new Date(start))
    end.setDate(start.getDate() + 6)
    const format = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${format(start)} - ${format(end)}`
  }, [weekOffset])

  const maxBarMinutes = useMemo(
    () => Math.max(...barChartData.map(day => day.totalMinutes), 0),
    [barChartData]
  )
  const peakDayMinutes = useMemo(
    () => (barChartData.length ? Math.max(...barChartData.map(day => day.totalMinutes)) : 0),
    [barChartData]
  )
  const hasBarActivity = useMemo(
    () => barChartData.some(day => day.totalMinutes > 0),
    [barChartData]
  )

  // Get language name for display
  const getLanguageName = (code: string) => {
    const lang = availableLanguages.find(l => l.code === code)
    return lang?.name || code
  }
  const currentLanguageName = currentLanguage ? getLanguageName(currentLanguage) : 'Current Language'
  const activePlanLanguageName = activePlan?.plan ? getLanguageName(activePlan.plan.languageCode) : null
  const shouldShowLanguageMismatchNotice = Boolean(
    activePlan?.plan && currentLanguage && activePlan.plan.languageCode !== currentLanguage
  )
  const shouldShowActivePlan = Boolean(
    activePlan?.plan && (!currentLanguage || activePlan.plan.languageCode === currentLanguage || showOtherLanguagePlan)
  )
  const shouldShowCreatePlanForm = Boolean(!activePlan?.plan || forceShowCurrentLanguagePlanForm)
  const studyAnalyticsSection = (
    <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-[var(--color-text)]">Study Analytics</h3>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Yearly activity heatmap plus daily minutes (7-day view; navigate weeks below).
          </p>
        </div>
      </div>

      {logsError && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-xl mb-4">
          {logsError}
        </div>
      )}

      {isLoadingLogs ? (
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Yearly tracker</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Up to the last 12 months of study days</p>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {heatmapData.weeks.length ? `${heatmapData.weeks.length} wks` : 'No data yet'}
              </span>
            </div>
            {/* Mobile calendar-style heat map */}
            <div className="md:hidden pb-2">
              {mobileHeatmapMonths.length === 0 ? (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                  No study activity recorded yet.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="pl-12 pr-2 flex justify-between text-[11px] uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">
                    {DAY_INITIALS.map((label, idx) => (
                      <span key={`${label}-${idx}`} className="w-6 text-center">
                        {label}
                      </span>
                    ))}
                  </div>
                  {mobileHeatmapMonths.map(month => (
                    <div key={month.key} className="flex items-start gap-2">
                      <div className="w-10 text-[11px] font-semibold text-gray-500 dark:text-gray-400 pt-1">
                        {month.label}
                      </div>
                      <div className="grid grid-cols-7 gap-1 flex-1">
                        {month.days.map((day, dayIdx) =>
                          day.date ? (
                            <button
                              type="button"
                              key={day.date}
                              className={`w-6 h-6 rounded border border-white shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] ${getHeatmapClass(
                                day.minutes,
                              )}`}
                              title={`${day.date}: ${day.minutes} min total`}
                              onClick={() => setSelectedDayDetail(buildDayDetail(studyLogs, day.date))}
                            />
                          ) : (
                            <span key={`empty-${month.key}-${dayIdx}`} className="w-6 h-6 rounded" aria-hidden="true" />
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="hidden md:block pb-2">
              {/* Month Labels */}
              <div className="flex gap-1 text-xs text-gray-400 mb-1 h-4 relative">
                {heatmapData.weeks.map((week, weekIdx) => {
                  const firstDay = new Date(week[0].date)
                  const prevWeek = heatmapData.weeks[weekIdx - 1]
                  const prevFirstDay = prevWeek ? new Date(prevWeek[0].date) : null

                  // Show label if it's the first week of the month
                  const showLabel = !prevFirstDay || firstDay.getMonth() !== prevFirstDay.getMonth()

                  return (
                    <div key={weekIdx} className="flex-1 min-w-[10px] relative">
                      {showLabel && (
                        <span className="absolute left-0 top-0 whitespace-nowrap text-[10px]">
                          {firstDay.toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Grid */}
              <div className="flex items-start gap-1 flex-nowrap">
                {heatmapData.weeks.map((week, weekIdx) => (
                  <div key={weekIdx} className="flex-1 grid grid-rows-7 gap-1 min-w-[10px]">
                    {week.map(day => (
                      <div
                        key={day.date}
                        className={`w-full aspect-square rounded-sm transition-colors hover:ring-1 hover:ring-offset-1 hover:ring-gray-400 cursor-pointer ${getHeatmapClass(day.minutes)}`}
                        title={`${day.date}: ${day.minutes} min total`}
                        onClick={() => setSelectedDayDetail(buildDayDetail(studyLogs, day.date))}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-3">
              <span>Less</span>
              <div className="flex gap-1">
                {[0, 10, 30, 60, 90].map(level => (
                  <span
                    key={level}
                    className={`w-3 h-3 rounded-sm ${getHeatmapClass(level)}`}
                  />
                ))}
              </div>
              <span>More</span>
            </div>

            {selectedDayDetail && (
              <div className="md:hidden mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {new Date(`${selectedDayDetail.date}T12:00:00`).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      weekday: 'short',
                    })}
                  </div>
                  <div className="text-gray-700 dark:text-gray-200 font-semibold">
                    {selectedDayDetail.totalMinutes || 0} min total
                  </div>
                </div>
                <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <span>Spoken: {selectedDayDetail.spokenMinutes} min</span>
                  <span>Flashcards: {selectedDayDetail.flashcardMinutes} min</span>
                </div>
                <div className="space-y-1">
                  {selectedDayDetail.languages.length === 0 ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400">No language breakdown for this day.</div>
                  ) : (
                    selectedDayDetail.languages.map(lang => (
                      <div key={lang.language} className="flex justify-between text-xs text-gray-700 dark:text-gray-200">
                        <span className="font-medium">{getLanguageName(lang.language)}</span>
                        <span>
                          {lang.total} min (Spoken {lang.spoken} / Flashcards {lang.flash})
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Daily minutes</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Breakdown by activity (Conversation vs. Flashcards)</p>
              </div>
              <div className="flex flex-col items-end gap-1 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setWeekOffset(prev => prev - 1)}
                    className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Previous week"
                  >
                    {'<'}
                  </button>
                  <span className="font-medium text-gray-700 dark:text-gray-200">{currentWeekRangeLabel}</span>
                  <button
                    type="button"
                    onClick={() => setWeekOffset(prev => Math.min(prev + 1, 0))}
                    disabled={weekOffset === 0}
                    className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next week"
                  >
                    {'>'}
                  </button>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  Total: {barChartData.reduce((sum, day) => sum + day.totalMinutes, 0)} min
                </div>
              </div>
            </div>

            {!hasBarActivity ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No study activity recorded yet.</p>
            ) : (
              <div>
                <div className="flex items-end gap-2 h-48">
                  {barChartData.map(day => (
                    <div key={day.date} className="flex-1 group">
                      <div className="flex flex-col justify-end h-40 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden" title={`${day.label}: ${day.totalMinutes} min total`}>
                        {day.activities.map(segment => (
                          <div
                            key={segment.type}
                            className={`w-full transition-all duration-300 ${segment.color}`}
                            style={{
                              height: `${maxBarMinutes ? (segment.minutes / maxBarMinutes) * 100 : 0}%`,
                            }}
                            title={`${day.label} - ${segment.label}: ${segment.minutes} min`}
                          />
                        ))}
                      </div>
                      <div className="text-center text-xs text-gray-600 dark:text-gray-300 mt-1">
                        {day.label}
                      </div>
                      <div className="text-center text-[11px] text-gray-500 dark:text-gray-400">
                        {day.totalMinutes}m
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-3 flex-wrap justify-center">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-blue-500" />
                    <span>Conversation</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-purple-500" />
                    <span>Flashcards</span>
                  </div>
                  <div className="ml-auto text-gray-600 dark:text-gray-300">
                    Peak day: {peakDayMinutes} min
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const activePlanContent = activePlan?.plan ? (
    <div className="space-y-6 mb-8">
      {/* Progress Card */}
      <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
              Active Study Plan - {getLanguageName(activePlan.plan.languageCode)}
            </h2>
            <p className="text-[var(--color-text-secondary)]">
              Goal: {activePlan.plan.goalHours} hours by {formatDate(activePlan.plan.targetDate)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={isEditing ? stopEditingPlan : startEditingPlan}
              variant={isEditing ? 'secondary' : 'primary'}
              size="sm"
              type="button"
            >
              {isEditing ? 'Close Editor' : 'Edit Plan'}
            </Button>
            <Button
              onClick={handleCancelPlan}
              variant="secondary"
              size="sm"
              disabled={isSubmitting}
              type="button"
            >
              Cancel Plan
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[var(--color-text-secondary)] mb-2">
            <span>Progress</span>
            <span>{activePlan.stats.progressPercentage.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-[var(--color-border)] rounded-full h-4 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${activePlan.stats.isOnTrack
                ? 'bg-success-500'
                : activePlan.stats.progressPercentage > 0
                  ? 'bg-warning-500'
                  : 'bg-error-500'
                }`}
              style={{ width: `${Math.min(100, activePlan.stats.progressPercentage)}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[var(--color-surface-secondary)] rounded-xl p-4">
            <div className="text-2xl font-semibold text-[var(--color-text)]">
              {formatHours(activePlan.stats.hoursCompleted)}
            </div>
            <div className="text-sm text-[var(--color-muted)]">Completed</div>
          </div>
          <div className="bg-[var(--color-surface-secondary)] rounded-xl p-4">
            <div className="text-2xl font-semibold text-[var(--color-text)]">
              {formatHours(activePlan.stats.hoursRemaining)}
            </div>
            <div className="text-sm text-[var(--color-muted)]">Remaining</div>
          </div>
          <div className="bg-[var(--color-surface-secondary)] rounded-xl p-4">
            <div className="text-2xl font-semibold text-[var(--color-text)]">
              {activePlan.stats.daysRemaining}
            </div>
            <div className="text-sm text-[var(--color-muted)]">Days Left</div>
          </div>
          <div className="bg-[var(--color-surface-secondary)] rounded-xl p-4">
            <div className="text-2xl font-semibold text-[var(--color-text)]">
              {activePlan.plan.dailyStudyMinutes}
            </div>
            <div className="text-sm text-[var(--color-muted)]">Min/Day Target</div>
          </div>
        </div>

        {/* Status Message */}
        <div
          className={`p-4 rounded-xl ${activePlan.stats.isOnTrack
            ? 'bg-success-50 border border-success-200'
            : 'bg-warning-50 border border-warning-200'
            }`}
        >
          <p
            className={`font-medium ${activePlan.stats.isOnTrack
              ? 'text-success-700'
              : 'text-warning-700'
              }`}
          >
            {activePlan.stats.isOnTrack
              ? 'Great work! You\'re on track to reach your goal.'
              : 'You\'re falling behind schedule. Try to catch up with your daily target!'}
          </p>
        </div>

        {/* Study Days */}
        <div className="mt-6">
          <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">
            Your Study Days
          </h3>
          <div className="flex gap-2">
            {DAY_ABBREVIATIONS.map((day, idx) => (
              <div
                key={idx}
                className={`flex-1 text-center py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${activePlan.plan.studyDays.includes(idx)
                  ? 'bg-primary-500 text-white'
                  : 'bg-[var(--color-surface-secondary)] text-[var(--color-muted)]'
                  }`}
              >
                {day}
              </div>
            ))}
          </div>
        </div>

        {isEditing && (
          <form
            onSubmit={handleUpdatePlan}
            className="mt-8 space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Study Goal (hours)
              </label>
              <input
                type="number"
                min="1"
                max="5000"
                step="0.5"
                value={goalHours}
                onChange={e => setGoalHours(parseFloat(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              {renderGoalSuggestions(setGoalHours)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Date
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={e => setTargetDate(e.target.value)}
                min={getTodayInputValue()}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Study Days
              </label>
              <div className="grid grid-cols-7 gap-2">
                {DAY_NAMES.map((day, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`py-2 rounded-lg text-sm font-medium transition-colors ${selectedDays.includes(idx)
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                  >
                    <div className="hidden sm:block">{day}</div>
                    <div className="sm:hidden">{DAY_ABBREVIATIONS[idx]}</div>
                  </button>
                ))}
              </div>
            </div>

            {selectedDays.length > 0 && goalHours > 0 && targetDate && (() => {
              try {
                const studyDayCount = countStudyDays(new Date(), new Date(targetDate), selectedDays)
                const dailyMinutes = studyDayCount > 0 ? Math.ceil((goalHours * 60) / studyDayCount) : 0

                return (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-blue-800 dark:text-blue-200 font-medium">
                      Updating this plan means studying approximately <strong>{dailyMinutes} minutes</strong> per study day.
                    </p>
                  </div>
                )
              } catch {
                return null
              }
            })()}

            <div className="flex gap-3">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="secondary" onClick={stopEditingPlan}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      {studyAnalyticsSection}
    </div>
  ) : null

  function renderGoalSuggestions(setter: (value: number) => void) {
    return (
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300">Suggestions:</span>
        {GOAL_SUGGESTIONS.map(suggestion => (
          <button
            key={suggestion.hours}
            type="button"
            onClick={() => setter(suggestion.hours)}
            className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/40 border border-gray-200 dark:border-gray-600"
          >
            {suggestion.hours}h · {suggestion.label}
          </button>
        ))}
      </div>
    )
  }

  // Access gate (Free users don't have access)
  if (!hasAccess) {
    return (
      <div className="page-shell bg-[var(--color-bg)]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-semibold text-[var(--color-text)]">Study Plans</h1>
            <button
              onClick={() => navigate('/')}
              className="text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors duration-200"
            >
              &larr; Back to Home
            </button>
          </div>

          <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-8 text-center">
            <div className="mb-6">
              <svg
                className="w-20 h-20 mx-auto text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>
            <div className="inline-block bg-gradient-to-r from-primary-400 to-secondary-400 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
              STANDARD & PREMIUM FEATURE
            </div>
            <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-4">
              Unlock Study Plans
            </h2>
            <p className="text-[var(--color-text-secondary)] mb-6 max-w-md mx-auto">
              Set study hour goals, choose your study days, and get personalized daily targets to reach your goals on time.
            </p>

            {/* Feature List */}
            <div className="max-w-md mx-auto mb-8 text-left">
              <div className="space-y-3">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-success-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div className="font-medium text-[var(--color-text)]">Set Custom Goals</div>
                    <div className="text-sm text-[var(--color-muted)]">Choose your target hours and deadline</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-success-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div className="font-medium text-[var(--color-text)]">Flexible Scheduling</div>
                    <div className="text-sm text-[var(--color-muted)]">Pick which days you want to study</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-success-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div className="font-medium text-[var(--color-text)]">Track Progress</div>
                    <div className="text-sm text-[var(--color-muted)]">Visual stats show if you're on track</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-success-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div className="font-medium text-[var(--color-text)]">Smart Recommendations</div>
                    <div className="text-sm text-[var(--color-muted)]">Get personalized daily study targets</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button onClick={() => navigate('/pricing')} variant="primary" size="lg">
                Upgrade to Access
              </Button>
              <div>
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors duration-200"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <div className="page-shell bg-[var(--color-bg)]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-semibold text-[var(--color-text)]">Study Plans</h1>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors duration-200"
            >
              &larr; Back to Home
            </button>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          {shouldShowLanguageMismatchNotice && activePlanLanguageName && (
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <p className="text-base font-semibold text-primary-700">
                  {currentLanguageName} Study Plan
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="sm"
                    onClick={() => {
                      setForceShowCurrentLanguagePlanForm(true)
                      setShowOtherLanguagePlan(false)
                    }}
                  >
                    Add {currentLanguageName} Plan
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setShowOtherLanguagePlan(true)
                      setForceShowCurrentLanguagePlanForm(false)
                    }}
                  >
                    View {activePlanLanguageName} Plan
                  </Button>
                  {showOtherLanguagePlan && (
                    <button
                      type="button"
                      onClick={() => setShowOtherLanguagePlan(false)}
                      className="text-sm text-primary-700 underline underline-offset-2 hover:text-primary-500 transition-colors duration-200"
                    >
                      Hide plan details
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Active Study Plan Display */}
          {shouldShowActivePlan && activePlanContent}

          {/* Create Study Plan Form - Show when no active plan */}
          {shouldShowCreatePlanForm && (
            <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
                  Add Study Plan for {currentLanguageName}
                </h2>
                {!currentLanguage && (
                  <p className="text-sm text-warning-600">
                    Please select a language from the home page to create a study plan
                  </p>
                )}
              </div>

              <form onSubmit={handleCreatePlan} className="space-y-6">
                {/* Calculation Mode Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Planning Mode
                  </label>
                  <div className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 p-1">
                    <button
                      type="button"
                      onClick={() => setCalculationMode('forward')}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition ${calculationMode === 'forward'
                        ? 'bg-white dark:bg-gray-800 shadow text-blue-600'
                        : 'text-gray-600 dark:text-gray-300'
                        }`}
                    >
                      Set End Date
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalculationMode('reverse')}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition ${calculationMode === 'reverse'
                        ? 'bg-white dark:bg-gray-800 shadow text-blue-600'
                        : 'text-gray-600 dark:text-gray-300'
                        }`}
                    >
                      Set Daily Minutes
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {calculationMode === 'forward'
                      ? 'Choose your deadline and we\'ll calculate daily study time'
                      : 'Set your daily practice time and we\'ll calculate when you\'ll finish'}
                  </p>
                </div>

                {/* Goal Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Study Goal (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5000"
                    step="0.5"
                    value={goalHours}
                    onChange={e => setGoalHours(parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    How many hours do you want to study in total?
                  </p>
                  {renderGoalSuggestions(setGoalHours)}
                </div>

                {/* Conditional Input: Target Date OR Daily Minutes */}
                {calculationMode === 'forward' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Target Date
                    </label>
                    <input
                      type="date"
                      value={targetDate}
                      onChange={e => setTargetDate(e.target.value)}
                      min={getTodayInputValue()}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      When do you want to achieve this goal?
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Daily Study Minutes
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1440"
                      step="1"
                      value={dailyMinutes}
                      onChange={e => setDailyMinutes(parseInt(e.target.value, 10))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      How many minutes per day do you want to practice?
                    </p>
                  </div>
                )}

                {/* Study Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Study Days
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {DAY_NAMES.map((day, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleDay(idx)}
                        className={`py-3 rounded-lg text-sm font-medium transition-colors ${selectedDays.includes(idx)
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                      >
                        <div className="hidden sm:block">{day}</div>
                        <div className="sm:hidden">{DAY_ABBREVIATIONS[idx]}</div>
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Select the days you plan to study each week
                  </p>
                </div>

                {/* Calculated Result Preview */}
                {selectedDays.length > 0 && goalHours > 0 && (
                  calculationMode === 'forward' && targetDate ? (
                    (() => {
                      try {
                        const studyDayCount = countStudyDays(new Date(), new Date(targetDate), selectedDays);
                        const dailyMinutes = studyDayCount > 0 ? Math.ceil((goalHours * 60) / studyDayCount) : 0;

                        return (
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <p className="text-blue-800 dark:text-blue-200 font-medium">
                              Based on your selections, you'll need to study approximately{' '}
                              <strong>{dailyMinutes} minutes</strong> per study day to reach your goal.
                            </p>
                          </div>
                        );
                      } catch (error) {
                        return null;
                      }
                    })()
                  ) : calculationMode === 'reverse' && dailyMinutes > 0 ? (
                    (() => {
                      try {
                        const totalMinutes = goalHours * 60;
                        const studyDaysNeeded = Math.ceil(totalMinutes / dailyMinutes);

                        // Calculate target date by counting only selected study days
                        let studyDaysCount = 0;
                        const startDate = new Date();
                        const current = new Date();

                        // Start from tomorrow to avoid counting today
                        current.setDate(current.getDate() + 1);

                        while (studyDaysCount < studyDaysNeeded) {
                          const dayOfWeek = current.getDay();
                          if (selectedDays.includes(dayOfWeek)) {
                            studyDaysCount++;
                            if (studyDaysCount >= studyDaysNeeded) {
                              break;
                            }
                          }
                          current.setDate(current.getDate() + 1);
                        }

                        // Calculate calendar days from today to target date
                        const calendarDays = Math.ceil((current.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

                        return (
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <p className="text-blue-800 dark:text-blue-200 font-medium">
                              With {dailyMinutes} minutes per day, you'll reach your goal on{' '}
                              <strong>{current.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                              {' '}({calendarDays} days).
                            </p>
                          </div>
                        );
                      } catch (error) {
                        return null;
                      }
                    })()
                  ) : null
                )}

                {/* Form Actions */}
                <div>
                  <Button type="submit" variant="primary" disabled={isSubmitting} size="lg">
                    {isSubmitting ? 'Creating...' : 'Create Study Plan'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {!shouldShowActivePlan && studyAnalyticsSection}
        </div>
      </div>
    </>
  )
}

type LanguageBarBreakdown = {
  language: string
  spokenMinutes: number
  flashcardMinutes: number
  totalMinutes: number
}

type ActivityType = 'conversation' | 'flashcards'

type ActivityBarSegment = {
  type: ActivityType
  minutes: number
  label: string
  color: string
}

type BarSeriesPoint = {
  date: string
  label: string
  activities: ActivityBarSegment[]
  totalMinutes: number
}

interface HeatmapData {
  weeks: Array<Array<{ date: string; minutes: number }>>;
}

interface MobileHeatmapMonth {
  key: string;
  label: string;
  days: Array<{ date: string | null; minutes: number }>;
}

function buildHeatmap(logs: DailyStudyLog[]): HeatmapData {
  const logMap = new Map(logs.map(log => [normalizeLogDateToLocal(log.date), log]))
  const today = startOfDay(new Date())
  const start = startOfDay(new Date(today))
  start.setDate(today.getDate() - 364)
  start.setDate(start.getDate() - start.getDay()) // align to start of week

  const weeks: HeatmapData['weeks'] = []
  let cursor = start

  while (cursor <= today) {
    const week: HeatmapData['weeks'][number] = []
    for (let i = 0; i < 7; i += 1) {
      const iso = toISODate(cursor)
      week.push({
        date: iso,
        minutes: getTotalMinutesFromLog(logMap.get(iso)),
      })
      cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000)
    }
    weeks.push(week)
  }

  return { weeks }
}

function buildMobileHeatmapMonths(logs: DailyStudyLog[]): MobileHeatmapMonth[] {
  const logMap = new Map(logs.map(log => [normalizeLogDateToLocal(log.date), log]))
  const today = startOfDay(new Date())
  const start = startOfDay(new Date(today))
  start.setDate(today.getDate() - 364)

  const months: MobileHeatmapMonth[] = []
  let cursor = new Date(start)
  let currentMonth: MobileHeatmapMonth | null = null

  while (cursor <= today) {
    const monthKey = `${cursor.getFullYear()}-${cursor.getMonth()}`
    if (!currentMonth || currentMonth.key !== monthKey) {
      currentMonth = {
        key: monthKey,
        label: cursor.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
        days: [],
      }
      const offset = cursor.getDay()
      for (let i = 0; i < offset; i += 1) {
        currentMonth.days.push({ date: null, minutes: 0 })
      }
      months.push(currentMonth)
    }

    const iso = toISODate(cursor)
    currentMonth.days.push({
      date: iso,
      minutes: getTotalMinutesFromLog(logMap.get(iso)),
    })

    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000)
  }

  months.forEach(month => {
    const remainder = month.days.length % 7
    if (remainder !== 0) {
      for (let i = 0; i < 7 - remainder; i += 1) {
        month.days.push({ date: null, minutes: 0 })
      }
    }
  })

  const MAX_MONTHS = 12
  return months.length > MAX_MONTHS ? months.slice(months.length - MAX_MONTHS) : months
}

function buildBarSeries(logs: DailyStudyLog[], window: 'week' | 'month', weekOffset = 0): BarSeriesPoint[] {
  const logsByDate = new Map<string, DailyStudyLog[]>()
  logs.forEach(log => {
    const normalizedDate = normalizeLogDateToLocal(log.date)
    const arr = logsByDate.get(normalizedDate) || []
    arr.push(log)
    logsByDate.set(normalizedDate, arr)
  })

  if (window === 'week') {
    const days = 7
    const start = startOfWeekSunday(new Date())
    start.setDate(start.getDate() + weekOffset * 7)
    const end = startOfDay(new Date(start))
    end.setDate(start.getDate() + (days - 1))

    const data: BarSeriesPoint[] = []
    for (let i = 0; i < days; i += 1) {
      const date = startOfDay(new Date(start))
      date.setDate(start.getDate() + i)
      const iso = toISODate(date)
      const dayLogs = logsByDate.get(iso) || []

      let totalSpoken = 0
      let totalFlash = 0

      dayLogs.forEach(log => {
        const spokenMinutes = getSpokenMinutesFromLog(log)
        const flashcardMinutes = getFlashcardMinutesFromLog(log)

        if (log.languageBreakdown) {
          Object.values(log.languageBreakdown).forEach(breakdown => {
            totalSpoken += Math.round((breakdown.conversationTimeSeconds || 0) / 60)
            totalFlash += Math.round((breakdown.flashcardStudySeconds || 0) / 60)
          })
        } else {
          totalSpoken += spokenMinutes
          totalFlash += flashcardMinutes
        }
      })

      data.push({
        date: iso,
        label: date.toLocaleDateString('en-US', { weekday: 'short' }),
        activities: [
          { type: 'conversation', minutes: totalSpoken, label: 'Conversation', color: 'bg-blue-500' },
          { type: 'flashcards', minutes: totalFlash, label: 'Flashcards', color: 'bg-purple-500' }
        ],
        totalMinutes: totalSpoken + totalFlash,
      })
    }
    return data
  }

  // Month window
  const days = 30
  const end = startOfDay(new Date())
  const start = startOfDay(new Date(end))
  start.setDate(end.getDate() - (days - 1))

  const data: BarSeriesPoint[] = []
  for (let i = 0; i < days; i += 1) {
    const date = startOfDay(new Date(start))
    date.setDate(start.getDate() + i)
    const iso = toISODate(date)
    const dayLogs = logsByDate.get(iso) || []

    let totalSpoken = 0
    let totalFlash = 0

    dayLogs.forEach(log => {
      const spokenMinutes = getSpokenMinutesFromLog(log)
      const flashcardMinutes = getFlashcardMinutesFromLog(log)

      if (log.languageBreakdown) {
        Object.values(log.languageBreakdown).forEach(breakdown => {
          totalSpoken += Math.round((breakdown.conversationTimeSeconds || 0) / 60)
          totalFlash += Math.round((breakdown.flashcardStudySeconds || 0) / 60)
        })
      } else {
        totalSpoken += spokenMinutes
        totalFlash += flashcardMinutes
      }
    })

    data.push({
      date: iso,
      label: `${date.getMonth() + 1}/${date.getDate()}`,
      activities: [
        { type: 'conversation', minutes: totalSpoken, label: 'Conversation', color: 'bg-blue-500' },
        { type: 'flashcards', minutes: totalFlash, label: 'Flashcards', color: 'bg-purple-500' }
      ],
      totalMinutes: totalSpoken + totalFlash,
    })
  }
  return data
}

function getTotalMinutesFromLog(log?: DailyStudyLog): number {
  if (!log) return 0
  const studySeconds = log.studyTimeSeconds ?? 0
  const flashSeconds = log.flashcardStudySeconds ?? 0
  const conversationSeconds =
    log.conversationTimeSeconds ?? (!flashSeconds ? studySeconds : 0)
  const totalSeconds = studySeconds || conversationSeconds + flashSeconds
  return Math.round(totalSeconds / 60)
}

function getSpokenMinutesFromLog(log?: DailyStudyLog): number {
  if (!log) return 0
  const conversationSeconds =
    log.conversationTimeSeconds ?? (log.flashcardStudySeconds ? 0 : log.studyTimeSeconds ?? 0)
  return Math.round((conversationSeconds || 0) / 60)
}

function getFlashcardMinutesFromLog(log?: DailyStudyLog): number {
  if (!log) return 0
  return Math.round((log.flashcardStudySeconds || 0) / 60)
}

function getHeatmapClass(minutes: number): string {
  // Anki-like colors
  if (minutes === 0) return 'bg-[#ebedf0] dark:bg-[#161b22]' // Empty
  if (minutes < 10) return 'bg-[#9be9a8] dark:bg-[#0e4429]' // Level 1
  if (minutes < 30) return 'bg-[#40c463] dark:bg-[#006d32]' // Level 2
  if (minutes < 60) return 'bg-[#30a14e] dark:bg-[#26a641]' // Level 3
  return 'bg-[#216e39] dark:bg-[#39d353]' // Level 4 (Max)
}

function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function startOfWeekSunday(date: Date): Date {
  const start = startOfDay(date)
  start.setDate(start.getDate() - start.getDay())
  return start
}

function toISODate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function normalizeLogDateToLocal(isoDate: string): string {
  if (!isoDate) return ''
  // If it's already YYYY-MM-DD, just return it
  if (isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) return isoDate
  // Otherwise take the date part
  return isoDate.split('T')[0]
}

function buildDayDetail(logs: DailyStudyLog[], date: string) {
  const normalizedDate = normalizeLogDateToLocal(date)
  const dayLogs = logs.filter(log => normalizeLogDateToLocal(log.date) === normalizedDate)
  const languageTotals = new Map<string, { spoken: number; flash: number; total: number }>()
  let spokenMinutes = 0
  let flashcardMinutes = 0
  let totalMinutes = 0

  dayLogs.forEach(log => {
    if (log.languageBreakdown && Object.keys(log.languageBreakdown).length > 0) {
      Object.entries(log.languageBreakdown).forEach(([language, breakdown]) => {
        const spoken = Math.round((breakdown.conversationTimeSeconds || 0) / 60)
        const flash = Math.round((breakdown.flashcardStudySeconds || 0) / 60)
        const total =
          Math.round(
            (breakdown.studyTimeSeconds ??
              (breakdown.conversationTimeSeconds || 0) + (breakdown.flashcardStudySeconds || 0)) / 60
          ) || spoken + flash
        const current = languageTotals.get(language) || { spoken: 0, flash: 0, total: 0 }
        languageTotals.set(language, {
          spoken: current.spoken + spoken,
          flash: current.flash + flash,
          total: current.total + total,
        })
        spokenMinutes += spoken
        flashcardMinutes += flash
        totalMinutes += total
      })
      return
    }

    const language = log.targetLanguage || 'unknown'
    const spoken = getSpokenMinutesFromLog(log)
    const flash = getFlashcardMinutesFromLog(log)
    const total = getTotalMinutesFromLog(log) || spoken + flash
    const current = languageTotals.get(language) || { spoken: 0, flash: 0, total: 0 }
    languageTotals.set(language, {
      spoken: current.spoken + spoken,
      flash: current.flash + flash,
      total: current.total + total,
    })
    spokenMinutes += spoken
    flashcardMinutes += flash
    totalMinutes += total
  })

  const languages = Array.from(languageTotals.entries()).map(([language, totals]) => ({
    language,
    spoken: totals.spoken,
    flash: totals.flash,
    total: totals.total,
  }))

  return {
    date: normalizedDate,
    languages,
    totalMinutes,
    spokenMinutes,
    flashcardMinutes,
  }
}

// Helper function to count study days between two dates
function countStudyDays(start: Date, end: Date, studyDays: number[]): number {
  let count = 0
  const current = new Date(start)

  while (current <= end) {
    if (studyDays.includes(current.getDay())) {
      count++
    }
    current.setDate(current.getDate() + 1)
  }

  return Math.max(1, count) // Prevent division by zero
}

function getDefaultTargetDate(): string {
  const date = new Date()
  date.setDate(date.getDate() + 30)
  return date.toISOString().split('T')[0]
}

function getTodayInputValue(): string {
  return new Date().toISOString().split('T')[0]
}

function formatDateForInput(dateString: string): string {
  if (!dateString) return ''
  return dateString.includes('T') ? dateString.split('T')[0] : dateString
}
